import {inject, Injectable} from '@angular/core';
import {AttributeFulfillmentTypeDirectus, FulfillmentType} from '@frontend/directus';
import {
  EvaluationCriteriaWithScores,
  ImportanceScoreView,
  ProviderScoreInfo,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {SubCriteriaItem} from '../../model/sub-criteria.interfaces';
import {DebugLogger} from '../../utils/debug-logger.util';
import {
  FieldCombination,
  ScoreCalculationContext,
  ScoreNormalizationResult,
  SubCriteriaAttributeMap,
} from './models';

@Injectable({providedIn: 'root'})
export class ScoreCalculationService {
  private readonly toastService = inject(ToastService);

  processScoreCalculations(context: ScoreCalculationContext): Map<string, FieldCombination> {
    const scoresByAttribute = this.groupScoresByAttribute(
      context.allScores,
      context.scoreToAttributeMap,
    );

    const normalizationResult = this.normalizeScores(
      scoresByAttribute,
      context.attributesMap,
      context.fulfillmentTypes,
    );

    const fieldGroups = this.groupScoresByField(
      context.allScores,
      context.allSubCriteriaItems,
      context.attributesMap,
      context.scoreToAttributeMap,
    );

    const fieldSums = this.calculateFieldSums(
      fieldGroups,
      normalizationResult.normalizedScores,
      context,
    );

    const result = this.createFieldCombinations(fieldGroups, fieldSums);

    return result;
  }

  extractImportanceScoresFromCriteria(
    criteria: EvaluationCriteriaWithScores[],
  ): Map<string, ImportanceScoreView[]> {
    const attributeImportanceScores = new Map<string, ImportanceScoreView[]>();

    criteria
      .flatMap((criterion) => criterion.subCriteria)
      .flatMap((subCriterion) => subCriterion.attributes)
      .filter((attribute) => attribute.importanceScores.length > 0)
      .forEach((attribute) => {
        attributeImportanceScores.set(attribute.directusId, attribute.importanceScores);
      });

    return attributeImportanceScores;
  }

  getFieldCombination(
    fieldCombinations: Map<string, FieldCombination>,
    scNumber: number,
    hrToolDirectusId: string,
  ): FieldCombination | undefined {
    return fieldCombinations.get(this.createFieldKey(scNumber, hrToolDirectusId));
  }

  calculateProviderTotal(
    fieldCombinations: Map<string, FieldCombination>,
    subCriteria: SubCriteriaItem[],
    hrToolDirectusId: string,
    scDistribution?: {
      id: number;
      percentage: number;
      directusId: string;
    }[],
  ): {total: number; maxPossibleScore: number} {
    const includedSubCriteria = subCriteria.filter((sc) => sc.included);
    let total = 0;
    let maxPossibleScoreTotal = 0;
    const scoreBreakdown: any[] = [];

    includedSubCriteria.forEach((subCriterion) => {
      const combination = this.getFieldCombination(
        fieldCombinations,
        subCriterion.scNumber,
        hrToolDirectusId,
      );

      const score = combination?.normalizedScore ?? 0;
      const maxPossible = combination?.maxPossibleScore ?? 0;
      const weight =
        scDistribution?.find((d) => d.directusId === subCriterion.directusId)?.percentage ?? 1;

      total += score * weight;
      maxPossibleScoreTotal += maxPossible * weight;

      scoreBreakdown.push({
        scNumber: subCriterion.scNumber,
        directusId: subCriterion.directusId,
        score,
        foundCombination: !!combination,
      });
    });

    return {total: total, maxPossibleScore: maxPossibleScoreTotal};
  }

  private groupScoresByAttribute(
    allScores: ProviderScoreInfo[],
    scoreToAttributeMap: Map<number, string>,
  ): Map<string, ProviderScoreInfo[]> {
    return allScores.reduce((scoresByAttribute, score) => {
      const attributeId = scoreToAttributeMap.get(score.id);
      if (!attributeId) {
        this.toastService.showError('errors.dataProcessing', 'errors.scoreMappingMissing');
        return scoresByAttribute;
      }

      const existingScores = scoresByAttribute.get(attributeId) || [];
      scoresByAttribute.set(attributeId, [...existingScores, score]);
      return scoresByAttribute;
    }, new Map<string, ProviderScoreInfo[]>());
  }

  private normalizeScores(
    scoresByAttribute: Map<string, ProviderScoreInfo[]>,
    attributesMap: SubCriteriaAttributeMap,
    fulfillmentTypes: AttributeFulfillmentTypeDirectus[],
  ): ScoreNormalizationResult {
    const normalizedScores = new Map<number, number>();
    const normalizationApplied = new Map<number, boolean>();

    scoresByAttribute.forEach((scores, attributeId) => {
      const shouldNormalize = this.shouldNormalizeScores(
        attributeId,
        attributesMap,
        fulfillmentTypes,
      );

      if (shouldNormalize) {
        this.applyMinMaxNormalization(scores, normalizedScores);
        scores.forEach((score) => normalizationApplied.set(score.id, true));
      } else {
        this.useRawScores(scores, normalizedScores);
        scores.forEach((score) => normalizationApplied.set(score.id, false));
      }
    });

    return {normalizedScores, normalizationApplied};
  }

  private groupScoresByField(
    allScores: ProviderScoreInfo[],
    subCriteria: SubCriteriaItem[],
    attributesMap: SubCriteriaAttributeMap,
    scoreToAttributeMap: Map<number, string>,
  ): Map<
    string,
    {fieldKey: {scNumber: number; hrToolDirectusId: string}; scores: ProviderScoreInfo[]}
  > {
    const scoresByField = new Map<
      string,
      {fieldKey: {scNumber: number; hrToolDirectusId: string}; scores: ProviderScoreInfo[]}
    >();

    allScores.forEach((score) => {
      const attributeId = scoreToAttributeMap.get(score.id);
      if (!attributeId) {
        this.toastService.showError('errors.dataProcessing', 'errors.scoreMappingMissing');
        return;
      }

      const scNumber = this.getSubCriteriaNumberForAttribute(
        attributeId,
        subCriteria,
        attributesMap,
      );
      if (scNumber === null) {
        this.toastService.showError('errors.dataProcessing', 'errors.subCriteriaMappingMissing');
        return;
      }

      const fieldKeyString = this.createFieldKey(scNumber, score.hrToolDirectusId);
      const fieldKey = {scNumber, hrToolDirectusId: score.hrToolDirectusId};

      if (!scoresByField.has(fieldKeyString)) {
        scoresByField.set(fieldKeyString, {fieldKey, scores: []});
      }
      scoresByField.get(fieldKeyString)?.scores.push(score);
    });

    return scoresByField;
  }

  private calculateFieldSums(
    scoresByField: Map<
      string,
      {fieldKey: {scNumber: number; hrToolDirectusId: string}; scores: ProviderScoreInfo[]}
    >,
    normalizedScores: Map<number, number>,
    context: ScoreCalculationContext,
  ): Map<string, {sum: number; maxPossibleSum: number}> {
    const fieldSums = new Map<string, {sum: number; maxPossibleSum: number}>();

    // subcriteria
    scoresByField.forEach((fieldData, fieldKeyString) => {
      let weightedSum = 0;
      let currentMaxValue = 0;

      //attribute score
      fieldData.scores.forEach((score) => {
        const normalizedScore = normalizedScores.get(score.id) ?? 0;
        const attributeDirectusId = context.scoreToAttributeMap.get(score.id);

        if (attributeDirectusId) {
          const importanceScores = context.attributeImportanceScores.get(attributeDirectusId) || [];
          const weights = importanceScores
            .map((importanceScore) =>
              context.importanceScoreWeights.get(importanceScore.importanceScore),
            )
            .filter((weight): weight is number => weight !== undefined);

          const attributeWeight =
            weights.length > 0
              ? weights.reduce((sum, weight) => sum + weight, 0) / weights.length
              : 1.0;

          currentMaxValue += attributeWeight;
          const weightedScore = normalizedScore * attributeWeight;
          weightedSum += weightedScore;
        } else {
          DebugLogger.warn('[ScoreCalculation] No attribute found for score', {scoreId: score.id});
          weightedSum += normalizedScore;
        }
      });

      fieldSums.set(fieldKeyString, {sum: weightedSum, maxPossibleSum: currentMaxValue});
    });

    return fieldSums;
  }

  private createFieldCombinations(
    fieldGroups: Map<
      string,
      {fieldKey: {scNumber: number; hrToolDirectusId: string}; scores: ProviderScoreInfo[]}
    >,
    fieldSums: Map<string, {sum: number; maxPossibleSum: number}>,
  ): Map<string, FieldCombination> {
    const fieldCombinations = new Map<string, FieldCombination>();

    fieldGroups.forEach((fieldGroup, fieldKeyString) => {
      const weightedSum = fieldSums.get(fieldKeyString) ?? {sum: 0, maxPossibleSum: 0};

      fieldCombinations.set(fieldKeyString, {
        scNumber: fieldGroup.fieldKey.scNumber,
        hrToolDirectusId: fieldGroup.fieldKey.hrToolDirectusId,
        normalizedScore: weightedSum.sum,
        maxPossibleScore: weightedSum.maxPossibleSum,
      });
    });

    return fieldCombinations;
  }

  private createFieldKey(scNumber: number, hrToolDirectusId: string): string {
    return `${scNumber}-${hrToolDirectusId}`;
  }

  private getSubCriteriaNumberForAttribute(
    attributeDirectusId: string,
    subCriteria: SubCriteriaItem[],
    attributesMap: SubCriteriaAttributeMap,
  ): number | null {
    const subCriteriaDirectusId = Object.entries(attributesMap).find(([, attributes]) =>
      attributes.some((attr) => attr.id === attributeDirectusId),
    )?.[0];

    if (!subCriteriaDirectusId) return null;

    return subCriteria.find((sc) => sc.directusId === subCriteriaDirectusId)?.scNumber ?? null;
  }

  private shouldNormalizeScores(
    attributeId: string,
    attributesMap: SubCriteriaAttributeMap,
    fulfillmentTypes: AttributeFulfillmentTypeDirectus[],
  ): boolean {
    const attribute = Object.values(attributesMap)
      .flat()
      .find((attr) => attr.id === attributeId);

    if (!attribute) {
      DebugLogger.warn('[ScoreCalculation] Attribute not found for normalization check', {
        attributeId,
      });
      return false;
    }

    const fulfillmentType = fulfillmentTypes.find(
      (ft) => ft.id === attribute.fulfillment_type,
    )?.type;

    const shouldNormalize =
      fulfillmentType === FulfillmentType.NUMERIC || fulfillmentType === FulfillmentType.PERCENTAGE;

    return shouldNormalize;
  }

  private applyMinMaxNormalization(
    scores: ProviderScoreInfo[],
    normalizedScores: Map<number, number>,
  ): void {
    if (scores.length === 0) {
      DebugLogger.warn('[ScoreCalculation] No scores to normalize');
      return;
    }

    const values = scores.map((s) => s.score);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;

    const normalizedValues: number[] = [];
    scores.forEach((score) => {
      const normalizedValue = this.calculateNormalizedValue(score.score, minValue, range);
      normalizedScores.set(score.id, normalizedValue);
      normalizedValues.push(normalizedValue);
    });
  }

  private useRawScores(scores: ProviderScoreInfo[], normalizedScores: Map<number, number>): void {
    scores.forEach((score) => {
      normalizedScores.set(score.id, score.score);
    });
  }

  private calculateNormalizedValue(score: number, minValue: number, range: number): number {
    if (range === 0) {
      return score === 0 ? 0 : 1;
    }
    return (score - minValue) / range;
  }
}
